import qs from 'qs';
import auth from '~/auth';

import { Brand } from '@luxuryescapes/lib-regions/lib/regions';

import { UTM_MEDIUM } from '~/consts/utm';

import ApiError from '~/types/ApiError';

import { sanitizeSchemaPatterns } from '~/utils/rjsfValidator';
import {
  type CustomerPropertyPatch,
  type CustomerPropertyValue,
  TRAVEL_PREFERENCE_KEYS,
} from '~/utils/travelPreferencesMapping';

import { customerPortalHost, json_headers, request } from './common';

const domains = {
  scoopon: window.configs.SCOOPON_API_DOMAIN,
  cudo: window.configs.CUDO_API_DOMAIN,
  treatme: window.configs.TREATME_API_DOMAIN,
  deals: window.configs.DEALS_API_DOMAIN,
  kogantravel: window.configs.KOGANTRAVEL_API_DOMAIN,
  lebusinesstraveller: window.configs.LEBUSINESSTRAVELLER_API_DOMAIN,
  leagenthub: window.configs.LEAGENTHUB_API_DOMAIN,
  leagenthub_cvfr: window.configs.LEAGENTHUB_CVFR_API_DOMAIN,
};

type RequestOptions = {
  signal?: AbortSignal;
};

function alphabetiseRoleList(roleEnums, roleEnumNames) {
  const sortedPairs = roleEnums
    .map((role, index) => [role, roleEnumNames[index]])
    .sort(([a], [b]) => a.localeCompare(b));

  const sortedRoleEnums = sortedPairs.map(([role]) => role);
  const sortedRoleEnumNames = sortedPairs.map(([, name]) => name);

  return { sortedRoleEnums, sortedRoleEnumNames };
}

export type IdentityProvider = {
  id: string;
  name: string;
  identifier_claim_name: string;
  auth_settings?: unknown;
};

export type AccountIdentity = {
  id: string;
  identity_provider_id: string;
  identifier_claim_value: string;
};

export type UserCustomClaim = {
  name: string;
  value: unknown;
};

export type UserCustomClaims = Array<UserCustomClaim>;

export type PatchUserPayload = {
  custom_claims?: UserCustomClaims;
};

type CustomerPropertyResponse<TValue = unknown> = {
  key: string;
  value: TValue;
  updated_at: string;
};

export default class UsersService {
  static basePath() {
    return window.configs.AUTH_HOST + '/api/users';
  }

  static async getImpersonationToken(customerId: string, brand?: string) {
    const accessToken = await auth.getAccessToken({
      impersonation: {
        customerId,
        brand,
      },
    });

    if (!accessToken) {
      throw new Error('You are not logged in.');
    }

    return accessToken;
  }

  static async getUser(id, opts?: { brand?: string; signal?: AbortSignal; cache?: RequestCache }) {
    const { brand, signal, cache } = opts || {};
    const query = qs.stringify({
      ...(brand ? { brand } : {}),
    });
    const jsonBody = await request(`${this.basePath()}/${id}${query ? `?${query}` : ''}`, {
      method: 'GET',
      ...(signal ? { signal } : {}),
      ...(cache ? { cache } : {}),
    });
    return jsonBody.result;
  }

  static async getUserByUrl(url) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}${url}`, { method: 'GET' });
    return jsonBody.result;
  }

  static async getUserSummary(id, brand) {
    const params = { brand };

    return await request(`${this.basePath()}/summary/${id}?${qs.stringify(params)}`, {
      method: 'GET',
    });
  }

  static async getUsersSummaryByIds(ids, brand) {
    const BATCH_SIZE = 15;
    const batches = [];

    // Split ids into batches to keep the summary endpoint fast and within its request limits.
    for (let i = 0; i < ids.length; i += BATCH_SIZE) {
      batches.push(ids.slice(i, i + BATCH_SIZE));
    }

    // Make parallel requests for each batch
    const batchPromises = batches.map((batch) => {
      const params = {
        brand,
        filter: batch,
      };

      return request(`${this.basePath()}/summary?${qs.stringify(params, { arrayFormat: 'repeat' })}`, {
        method: 'GET',
      });
    });

    const results = await Promise.all(batchPromises);
    const allUsers = results.flat();

    return new Map(
      allUsers.flatMap((user) => [user.id_member, user.id_account].filter(Boolean).map((userId) => [userId, user])),
    );
  }

  static async getUserTenancy(email) {
    let sanitizedUserEmail = email;

    // get rid of the # if its at the start of an email address
    // why? because this is being appended to a URL, and leaving it in will cause
    // it to be parsed as an anchor
    if (email.startsWith('#')) {
      sanitizedUserEmail = email.slice(1, email.length);
    }

    sanitizedUserEmail = encodeURIComponent(sanitizedUserEmail);

    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/get-user-tenants/${sanitizedUserEmail}`, {
      method: 'GET',
    });
    return jsonBody.result;
  }

  /**
   * Exact-match phone lookup, backed by the same customer lookup svc-auth uses for
   * login's phone-based "does this user exist" check, but gated on an employee token
   * instead of a public recaptcha token. Use this instead of `getUsers({ filter })`
   * when the phone number itself must be an authoritative signal (e.g. blocking a
   * duplicate account), since the filter-based search can miss or over-match.
   */
  static async getUserByPhone(
    phonePrefix: string,
    phone: string,
    options?: RequestOptions & { brand?: string },
  ): Promise<{ kind: 'none' } | { kind: 'multiple' } | { kind: 'single'; user: Record<string, unknown> }> {
    const { brand, signal } = options || {};
    const query = qs.stringify(brand ? { brand } : {});
    const response = await request(
      `${window.configs.AUTH_HOST}/api/users/${encodeURIComponent(phonePrefix)}/${encodeURIComponent(phone)}${
        query ? `?${query}` : ''
      }`,
      {
        method: 'GET',
        returnResponse: true,
        // Handle 404 (no match) and 422 (multiple matches) ourselves rather than letting
        // them fall through `request`'s default error path, which reports every 404 to
        // Sentry — "no user with this phone" is the expected, common outcome here.
        allowNonOkResponse: true,
        ...(signal ? { signal } : {}),
      },
    );

    if (response.status === 404) {
      return { kind: 'none' };
    }
    if (response.status === 422) {
      return { kind: 'multiple' };
    }
    if (!response.ok) {
      const body = await response.json().catch(() => ({}));
      throw new ApiError({
        message: body.message || `Could not look up user by phone (status ${response.status}).`,
        name: 'NetworkError',
        status: response.status,
      });
    }
    const user = await response.json();
    return { kind: 'single', user };
  }

  static async spoofUser(id, tenant, offerLink = '') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(tenant)}${offerLink}?utm_source=${
      tenant.brand
    }&utm_medium=admin&utm_campaign=spoof`;
  }

  static async spoofUserStore(id, tenant, offerLink = '', utmMedium = 'popup_store') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(
      tenant,
    )}${offerLink}?utm_medium=${utmMedium}&utm_source=${tenant.brand}&utm_campaign=spoof`;
  }

  static async spoofUserSearch(id, tenant) {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return {
      baseUrl: `${apiDomain}/redirect?token=${accessToken}`,
      queryParams: `utm_medium=popup_store&utm_source=${tenant.brand}&utm_campaign=spoof`,
    };
  }

  // utmMedium selects the sales channel the resulting order is attributed to,
  // matching spoofUser/spoofUserStore: UTM_MEDIUM ('admin') for the contact
  // centre, or a store's utmMedium from ~/consts/stores. It defaults to the
  // contact centre so callers that don't offer a channel choice are unchanged.
  static async spoofUserQuote(id, tenant, cartId, utmMedium = UTM_MEDIUM) {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${customerPortalHost(
      tenant,
    )}/checkout-restore-cart?cartId=${cartId}&utm_medium=${utmMedium}&utm_source=${
      tenant.brand
    }&utm_campaign=agent_purchased_quote`;
  }

  static async spoofUserTrip(id, tenant, tripLink = '') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    const destination = new URL(`${customerPortalHost(tenant)}${tripLink}`);
    destination.searchParams.set('utm_medium', 'popup_store');
    destination.searchParams.set('utm_source', tenant.brand);
    destination.searchParams.set('utm_campaign', 'spoof');
    return `${apiDomain}/redirect?token=${encodeURIComponent(accessToken)}&url=${encodeURIComponent(destination.toString())}`;
  }

  static async spoofUserFlightSearch(id: string, tenant: App.Tenant, searchUrl: string) {
    const destination = new URL(searchUrl);
    if (
      destination.origin !== new URL(customerPortalHost(tenant)).origin ||
      !destination.pathname.endsWith('/flights-search-results')
    ) {
      throw new Error('Invalid flight search destination.');
    }
    const accessToken = await this.getImpersonationToken(id, tenant.value);
    destination.searchParams.set('utm_medium', UTM_MEDIUM);
    destination.searchParams.set('utm_source', tenant.brand);
    destination.searchParams.set('utm_campaign', 'spoof');
    const redirect = new URL('/redirect', domains[tenant.value] || window.configs.LUX_API_DOMAIN);
    redirect.searchParams.set('token', accessToken);
    redirect.searchParams.set('url', destination.toString());
    return redirect.toString();
  }

  static async forceLogout(id, email, password) {
    await request(`${window.configs.AUTH_HOST}/api/users/${id}/invalidate-tokens`, {
      method: 'POST',
      headers: json_headers,
    });
    await request(`${window.configs.AUTH_HOST}/api/users/${id}`, {
      method: 'PUT',
      headers: json_headers,
      body: JSON.stringify({ email, password }),
    });
  }

  static async spoofUserWithOfferLink(id, offer, tenant) {
    const accessToken = await this.getImpersonationToken(id);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;
    const offer_url = customerPortalHost(tenant) + `/offer/${offer.slug}/${offer.id_salesforce_external}`;

    return `${apiDomain}/redirect?token=${accessToken}&url=${offer_url}?`;
  }

  static async spoofUserTokenOnly(id) {
    return this.getImpersonationToken(id);
  }

  static async spoofUserWithOfferDeepLink(id, tenant, offerLink = '', utmMedium = 'admin') {
    const accessToken = await this.getImpersonationToken(id, tenant.value);

    const apiDomain = domains[tenant.value] || window.configs.LUX_API_DOMAIN;

    return `${apiDomain}/redirect?token=${accessToken}&url=${offerLink}&utm_source=${tenant.brand}&utm_medium=${utmMedium}&utm_campaign=spoof`;
  }

  static getUserLogins(id, limit = 20) {
    return request(this.basePath() + '/logins/' + id + `?limit=${limit}`, { method: 'GET' });
  }

  static getUsers({ filter, limit = 50, offset = 0, brand }, options = undefined) {
    const queryParams = {
      filter,
      limit,
      offset,
      brand,
    };

    return request(this.basePath() + `?${qs.stringify(queryParams, { arrayFormat: 'repeat' })}`, options);
  }

  static getAdminUsers(filter, limit = 50, offset = 0) {
    const queryParams = {
      filter,
      limit,
      offset,
    };

    return request(`${window.configs.AUTH_HOST}/api/admin-users?${qs.stringify(queryParams)}`);
  }

  static getEmployeeUsers(filter?: string, limit = 20, offset = 0, roles?: Array<string>, options?: RequestOptions) {
    const queryParams: Record<string, string | number> = { limit, offset };
    if (filter != null && filter !== '') {
      queryParams.filter = filter;
    }
    if (roles?.length) {
      queryParams.roles = roles.join(',');
    }
    return request(`${window.configs.AUTH_HOST}/api/auth/employee-users?${qs.stringify(queryParams)}`, options);
  }

  static getVendorUsers(vendorId, limit = 50, offset = 0) {
    // TODO: Use qs.stringify
    return request(
      `${window.configs.AUTH_HOST}/api/vendor-users?vendor_id=${vendorId}&limit=${limit}&offset=${offset}`,
    );
  }

  static async patch(data) {
    const jsonBody = await request(`${this.basePath()}/${data.id_account}?${data.params}`, {
      method: 'PATCH',
    });
    return jsonBody.result;
  }

  static async patchUser(idMember: string, payload: PatchUserPayload) {
    const jsonBody = await request(`${this.basePath()}/${idMember}`, {
      method: 'PATCH',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
    return jsonBody.result as App.User;
  }

  static async getUpdateSchema(data, brand?: Brand) {
    const jsonBody = await request(`${this.basePath()}/${data.id_member}?brand=${brand}`, {
      method: 'OPTIONS',
    });

    if (jsonBody?.put?.properties?.roles?.items?.enum) {
      // svc-auth returns the roles in a random order, so we need to sort them for UX
      const { sortedRoleEnums, sortedRoleEnumNames } = alphabetiseRoleList(
        jsonBody.put.properties.roles.items.enum,
        jsonBody.put.properties.roles.items.enumNames,
      );

      jsonBody.put.properties.roles.items.enum = sortedRoleEnums;
      jsonBody.put.properties.roles.items.enumNames = sortedRoleEnumNames;
    }

    // Sanitize regex patterns in the schema to fix invalid escapes inside character classes
    if (jsonBody?.put) {
      jsonBody.put = sanitizeSchemaPatterns(jsonBody.put);
    }

    return jsonBody;
  }

  static async updateUser(data, brand) {
    const jsonBody = await request(`${this.basePath()}/${data.id_member}?brand=${brand}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async customerPropertyRequest(customerId: string, path: string, options: RequestInit) {
    return request(`${window.configs.AUTH_HOST}/api/customers/v1/${encodeURIComponent(customerId)}/properties${path}`, {
      ...options,
    });
  }

  static async getCustomerLanguagePreference(customerId: string): Promise<string | null> {
    try {
      const jsonBody = await this.customerPropertyRequest(customerId, '/language', { method: 'GET' });
      return (jsonBody?.result as CustomerPropertyResponse<string> | undefined)?.value ?? null;
    } catch (error) {
      if ((error as Error & { status?: number }).status === 404) {
        return null;
      }
      throw error;
    }
  }

  static async setCustomerLanguagePreference(customerId: string, language: string) {
    const jsonBody = await this.customerPropertyRequest(customerId, '/language', {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ value: language }),
    });
    return jsonBody?.result as CustomerPropertyResponse;
  }

  static async deleteCustomerLanguagePreference(customerId: string) {
    await this.customerPropertyRequest(customerId, '/language', { method: 'DELETE' });
  }

  static async getCustomerTravelPreferences(
    customerId: string,
    options?: { signal?: AbortSignal },
  ): Promise<Array<CustomerPropertyValue>> {
    const jsonBody = await this.customerPropertyRequest(customerId, `?keys=${TRAVEL_PREFERENCE_KEYS.join(',')}`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return (jsonBody?.result as Array<CustomerPropertyValue> | undefined) ?? [];
  }

  static async patchCustomerProperties(customerId: string, patches: Array<CustomerPropertyPatch>) {
    const jsonBody = await this.customerPropertyRequest(customerId, '', {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patches),
    });
    return (jsonBody?.result as Array<CustomerPropertyValue> | undefined) ?? [];
  }

  static async getUserBusinessDetails(idMember, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result;
  }

  static async updateUserBusinessDetails(idMember, data, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
      ...(options ?? {}),
    });
    return jsonBody.result;
  }

  static async deleteUserBusinessDetails(idMember) {
    return request(`${this.basePath()}/${idMember}/business-details`, {
      method: 'DELETE',
    });
  }

  static async toggle(data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/toggles`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async updatePartnershipsDetails(idMember, data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/partnerships/${idMember}`, {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static async getRegisterSchema(brand) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/register?brand=${brand}`, {
      method: 'OPTIONS',
    });

    // Sanitize regex patterns in the schema to fix invalid escapes inside character classes
    // that would fail with the Unicode flag used by AJV validator
    if (jsonBody?.post) {
      jsonBody.post = sanitizeSchemaPatterns(jsonBody.post);
    }

    return jsonBody;
  }

  static async registerUser(data) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/register`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(data),
    });
    return jsonBody.result;
  }

  static getRoles() {
    return request(`${window.configs.AUTH_HOST}/api/roles`);
  }

  static getUsersByRole(role, page = 1, filter?: string, limit?: number, options?: RequestOptions) {
    const queryParams: Record<string, string | number> = { role, page };
    if (filter) {
      queryParams.filter = filter;
    }
    if (limit) {
      queryParams.limit = limit;
    }

    return request(`${window.configs.AUTH_HOST}/api/roles/users?${qs.stringify(queryParams)}`, options);
  }

  static healthCheck() {
    return request(`${window.configs.AUTH_HOST}/api/auth/health`);
  }

  static async validateEmail(email) {
    return request(`${window.configs.AUTH_HOST}/api/validate-email?email=${email}`);
  }

  static async getEmailChangeHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/auth/email-change/email-change-history/${userId}`);
  }

  static async getPhoneNumberChangeHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/auth/phone-change/history/${userId}`);
  }

  static async getUserHistory(userId) {
    return request(`${window.configs.AUTH_HOST}/api/user-history/${userId}`);
  }

  static async getIdentityProviders(options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/identity-providers`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result as Array<IdentityProvider>;
  }

  static async getAccountIdentities(accountId: string, options?: { signal?: AbortSignal }) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities`, {
      method: 'GET',
      ...(options ?? {}),
    });
    return jsonBody.result as Array<AccountIdentity>;
  }

  static async createAccountIdentity(
    accountId: string,
    payload: { identity_provider_id: string; identifier_claim_value: string },
  ) {
    const jsonBody = await request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
    return jsonBody.result as AccountIdentity;
  }

  static async deleteAccountIdentity(accountId: string, identityId: string) {
    return request(`${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${accountId}/identities/${identityId}`, {
      method: 'DELETE',
    });
  }

  // Requires both admin-user and role-admin roles. Unlike the legacy
  // `PATCH /api/users/:id?is_enabled=`, these endpoints leave inline_roles
  // untouched, invalidate already-issued tokens on disable, and emit audit events.
  static async setAccountEnabled(accountId: string, enabled: boolean) {
    const jsonBody = await request(
      `${window.configs.AUTH_HOST}/api/auth/v2/admin/accounts/${encodeURIComponent(accountId)}/${
        enabled ? 'enable' : 'disable'
      }`,
      { method: 'POST' },
    );
    return jsonBody.result as { id: string; is_enabled: boolean };
  }

  static async sendOtpSmsCode(phone_prefix, phone, brand) {
    const payload = {
      brand,
      phone_number: {
        prefix: phone_prefix,
        phone,
      },
    };

    return request(`${window.configs.AUTH_HOST}/api/auth/otp/send-confirmation-code`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static async validateOtpSmsCode(phone_prefix, phone, code, brand) {
    const payload = {
      brand,
      code,
      phone_number: {
        prefix: phone_prefix,
        phone,
      },
    };

    return request(`${window.configs.AUTH_HOST}/api/auth/otp/validate-confirmation-code`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify(payload),
    });
  }

  static async getSmsVerificationStats(params?: { signal?: AbortSignal; startDate?: string; endDate?: string }) {
    const { signal, startDate, endDate } = params || {};
    const qs = new URLSearchParams();
    if (startDate) {
      qs.set('startDate', startDate);
    }
    if (endDate) {
      qs.set('endDate', endDate);
    }
    const query = qs.toString();
    const url = `${window.configs.AUTH_HOST}/api/auth/sms-verification/stats${query ? `?${query}` : ''}`;
    return request(url, { method: 'GET', ...(signal ? { signal } : {}) });
  }

  static async getSmsVerificationWarning(userId: string) {
    return request(`${window.configs.AUTH_HOST}/api/auth/sms-verification/warning/${userId}`, {
      method: 'GET',
    });
  }

  static async setSmsVerificationWarning(userId: string, warningType: 'soft' | 'hard' | 'none') {
    return request(`${window.configs.AUTH_HOST}/api/auth/sms-verification/warning/${userId}`, {
      method: 'POST',
      headers: json_headers,
      body: JSON.stringify({ warningType }),
    });
  }
}
